import { vrocManager, vrocFactory, VROCComponentFactory } from '../../_VROC/VROCComponentFactory.js';

export class Transfers {
	constructor() {
		this.activeComponent = null; // Активный компонент (если есть)
		this._debounceTimer = null; // Таймер для debounce
		this.orderId = this._getOrderId(); // Получаем ID заказа один раз

		// Настройки для времени выезда (Departure)
		this.departurePickupConfig = {
			hoursBeforeFlight: [2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6], // Массив часов до вылета
			stepMinutes: 30 // Шаг в минутах
		};

		this._registerComponent();
		this._initDeparturePickupListeners();
	}

	/**
	 * Инициализирует компонент (фоновая загрузка для attach режима)
	 */
	async init() {
		try {
			console.log('[Transfers] Initializing component in background');

			// Создаем компонент в фоне без лоадера
			await this._createComponent();

			console.log('[Transfers] Component initialized successfully');
		} catch (error) {
			console.error('[Transfers] Error during initialization:', error);
		}
	}

	/**
	 * Регистрирует базовую конфигурацию компонента в менеджере
	 */
	_registerComponent() {
		const baseConfig = {
			template: 'mod_crm/transfers_content',
			node: '.transfers__content',
			attach: true, // Режим attach - контент уже есть на странице
			moduleId: 39, // Модуль трансферов
			params: {
				action: 'loadItems', // Начальный запрос для attach режима
				...(this.orderId && { orderId: this.orderId }) // Добавляем orderId если он есть
			},
			autoActions: {
				updateTransfersList: {
					successMessage: 'Список трансферов обновлен',
					errorMessage: 'Ошибка обновления списка трансферов'
				}
			},
			validation: {},
			onError: (message, error) => {
				console.error('[Transfers] Component error:', message, error);
			},
			events: VROCComponentFactory.createEvents({
				beforeInit: () => {
					console.log('[Transfers] Component initialization started');
				},
				afterInit: () => {
					console.log('[Transfers] Component initialization completed');
				},
				beforeAction: (data) => {
					console.log('[Transfers] Component action started:', data.action);
				},
				afterAction: (data) => {
					console.log('[Transfers] Component action completed:', data.action);
				},
				error: (data) => {
					console.error('[Transfers] Component error occurred:', data.message);
				}
			}),
			notifications: {
				show: (message, type) => {
					console.log(`[Transfers] ${type.toUpperCase()}: ${message}`);
				}
			}
		};

		vrocManager.registerComponent('transfers', baseConfig);
	}

	// ===== ПУБЛИЧНЫЕ МЕТОДЫ ДЛЯ ИНЛАЙНОВЫХ СОБЫТИЙ =====

	/**
	 * Увеличивает количество взрослых
	 */
	increaseAdults() {
		const input = document.querySelector('.guests-picker__adults-input');
		if (input) {
			const currentValue = parseInt(input.value) || 0;
			input.value = currentValue + 1;
			this.updateData();
		}
	}

	/**
	 * Уменьшает количество взрослых
	 */
	decreaseAdults() {
		const input = document.querySelector('.guests-picker__adults-input');
		if (input) {
			const currentValue = parseInt(input.value) || 0;
			if (currentValue > 0) {
				input.value = currentValue - 1;
				this.updateData();
			}
		}
	}

	/**
	 * Обновляет данные с debounce (для текстовых полей)
	 */
	updateDataDebounced() {
		clearTimeout(this._debounceTimer);
		this._debounceTimer = setTimeout(() => {
			this.updateData();
		}, 500);
	}

	/**
	 * Основной метод обновления данных
	 */
	async updateData() {
		try {
			console.log('[Transfers] UpdateData called');

			// Создаем компонент если его нет
			if (!this.activeComponent) {
				await this._createComponent();
				return; // Первое создание уже загружает данные
			}

			// Показываем лоадер
			this._showLoader();

			// Обновляем данные
			await this._updateTransfersData();

		} catch (error) {
			console.error('[Transfers] Error updating data:', error);
		} finally {
			// Скрываем лоадер
			this._hideLoader();
		}
	}

	/**
	 * Публичный метод для обновления компонента (вызывается из app.js)
	 */
	async update() {
		await this.updateData();
	}

	// ===== DEPARTURE PICKUP TIME METHODS =====

	/**
	 * Инициализирует слушатели для полей даты и времени вылета
	 */
	_initDeparturePickupListeners() {
		// Используем делегирование событий на document
		document.addEventListener('change', (e) => {
			// Проверяем, изменились ли поля даты или времени вылета
			if (e.target.name === 'date_value' ||
			    e.target.name === 'tme_flighttime_h' ||
			    e.target.name === 'tme_flighttime_m') {
				this._updateDeparturePickupTime();
			}
		});

		// Слушаем клики по кнопкам выбора времени
		document.addEventListener('click', (e) => {
			if (e.target.closest('.form-time__choice-btn')) {
				// Даем время на обновление значения, потом пересчитываем
				setTimeout(() => this._updateDeparturePickupTime(), 100);
			}
		});

		// Слушаем изменения в datepicker
		document.addEventListener('datepicker:change', () => {
			this._updateDeparturePickupTime();
		});
	}

	/**
	 * Обновляет список времени выезда на основе даты и времени вылета
	 * Меняет только label элементов, value остается в часах
	 */
	_updateDeparturePickupTime() {
		// Проверяем, что мы на странице departure
		const choiceList = document.querySelector('.trf-departure-pickup-list');
		if (!choiceList) return;

		// Получаем дату вылета
		const dateInput = document.querySelector('input[name="date_value"]');
		if (!dateInput || !dateInput.value) return;

		// Получаем время вылета
		const flightHourInput = document.querySelector('input[name="tme_flighttime_h"]');
		const flightMinInput = document.querySelector('input[name="tme_flighttime_m"]');

		if (!flightHourInput || !flightMinInput) return;

		const flightHour = parseInt(flightHourInput.value);
		const flightMin = parseInt(flightMinInput.value);

		if (isNaN(flightHour) || isNaN(flightMin)) return;

		console.log('[Transfers] Updating departure pickup time labels for flight:', {
			date: dateInput.value,
			hour: flightHour,
			min: flightMin
		});

		// Создаем дату вылета
		// Парсим дату правильно - может быть в формате DD.MM.YYYY или YYYY-MM-DD
		let flightDate;
		const dateValue = dateInput.value;

		if (dateValue.includes('.')) {
			// Формат DD.MM.YYYY
			const [day, month, year] = dateValue.split('.').map(Number);
			flightDate = new Date(year, month - 1, day);
		} else if (dateValue.includes('-')) {
			// Формат YYYY-MM-DD (ISO)
			flightDate = new Date(dateValue);
		} else {
			console.error('[Transfers] Unknown date format:', dateValue);
			return;
		}

		flightDate.setHours(flightHour, flightMin, 0, 0);

		console.log('[Transfers] Parsed flight date:', flightDate.toLocaleString('ru-RU'));

		// Дата вылета для сравнения (только день)
		const flightDateOnly = new Date(flightDate);
		flightDateOnly.setHours(0, 0, 0, 0);

		// Получаем все radio кнопки
		const radioButtons = choiceList.querySelectorAll('input[type="radio"]');

		radioButtons.forEach(radio => {
			const hoursBefore = parseFloat(radio.value);
			if (isNaN(hoursBefore)) return;

			// Вычисляем время выезда
			const pickupDate = new Date(flightDate);
			pickupDate.setHours(pickupDate.getHours() - Math.floor(hoursBefore));
			pickupDate.setMinutes(pickupDate.getMinutes() - ((hoursBefore % 1) * 60));

			// Проверяем, отличается ли дата
			const pickupDateOnly = new Date(pickupDate);
			pickupDateOnly.setHours(0, 0, 0, 0);
			const isDifferentDay = pickupDateOnly.getTime() !== flightDateOnly.getTime();

			// Форматируем время
			const hours = String(pickupDate.getHours()).padStart(2, '0');
			const minutes = String(pickupDate.getMinutes()).padStart(2, '0');

			let label;
			if (isDifferentDay) {
				// Форматируем дату: "20 ноября"
				const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
				                'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
				const day = pickupDate.getDate();
				const month = months[pickupDate.getMonth()];
				label = `${day} ${month} в ${hours}:${minutes}`;
			} else {
				label = `${hours}:${minutes}`;
			}

			// Находим label для этого radio
			const labelEl = choiceList.querySelector(`label[for="${radio.id}"]`);
			if (labelEl) {
				labelEl.textContent = label;
			}

			// Если этот вариант выбран, обновляем верхнее поле
			if (radio.checked) {
				const topInput = document.querySelector('.form-multi__top-input[id="view_tme_pickuptime"]');
				if (topInput) topInput.value = label;
			}
		});

		console.log('[Transfers] Departure pickup time labels updated successfully');
	}

	// ===== ПРИВАТНЫЕ МЕТОДЫ =====

	/**
	 * Создает новый компонент
	 */
	async _createComponent() {
		if (this.activeComponent) {
			this.activeComponent = null;
		}

		const component = await vrocManager.createComponent('transfers');
		this.activeComponent = component;

		console.log('[Transfers] Component created successfully');
		return component;
	}

	/**
	 * Получает ID заказа со страницы
	 * @returns {string|null} ID заказа
	 */
	_getOrderId() {
		// Вариант 1: Из скрытого поля #orderId
		const orderIdElement = document.querySelector('#orderId');
		if (orderIdElement && orderIdElement.value) return orderIdElement.value;

		// Вариант 2: Из data-атрибута
		const dataElement = document.querySelector('[data-invoice-id]');
		if (dataElement) {
			const idFromData = dataElement.getAttribute('data-invoice-id');
			if (idFromData) return idFromData;
		}

		// Вариант 3: Из URL
		const urlParams = new URLSearchParams(window.location.search);
		const idFromUrl = urlParams.get('id');
		if (idFromUrl) return idFromUrl;

		return null;
	}

	/**
	 * Обновляет данные трансферов через AJAX
	 */
	async _updateTransfersData() {
		try {
			// Используем ServiceManager для получения данных формы
			if (typeof window.serviceManager === 'undefined') {
				console.error('[Transfers] serviceManager is not available');
				throw new Error('ServiceManager not available');
			}

			// Получаем данные из формы трансферов
			const transferFormData = window.serviceManager.getTransferFormData();

			// Создаем FormData из существующей формы
			const form = document.querySelector('.transfers__control');
			const formData = new FormData(form);

			// Добавляем данные гостей из ServiceManager
			formData.set('adults', transferFormData.adults || '1');
			formData.set('participants', transferFormData.participants || '1');

			// Добавляем детей как массив
			if (transferFormData.children && transferFormData.children.length > 0) {
				// Удаляем существующие записи children
				formData.delete('children');
				formData.delete('children[]');

				// Добавляем каждого ребенка отдельно
				transferFormData.children.forEach(childAge => {
					formData.append('children[]', childAge);
				});
			}

			// Добавляем action
			formData.set('action', 'loadItems');

			// Добавляем orderId если он есть
			if (this.orderId) {
				formData.set('orderId', this.orderId);
				console.log('[Transfers] Adding orderId to request:', this.orderId);
			}

			console.log('[Transfers] Sending AJAX request with data:', Array.from(formData.entries()));

			const response = await fetch(`/ajax.php?mid=39`, {
				method: 'POST',
				body: formData
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const xmlText = await response.text();
			console.log('[Transfers] AJAX response received');

			// Обновляем компонент новыми данными
			await this.activeComponent.vroc.updateComponent(this.activeComponent.id, xmlText);

		} catch (error) {
			console.error('[Transfers] Error updating transfers data:', error);
			throw error;
		}
	}

	/**
	 * Показывает лоадер
	 */
	_showLoader() {
		const loader = document.querySelector('.transfers__loader');
		if (loader) {
			loader.classList.add('active');
		}
	}

	/**
	 * Скрывает лоадер
	 */
	_hideLoader() {
		const loader = document.querySelector('.transfers__loader');
		if (loader) {
			loader.classList.remove('active');
		}
	}

	// ===== МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ КОМПОНЕНТОМ =====

	/**
	 * Уничтожает активный компонент
	 */
	destroyActiveComponent() {
		if (this.activeComponent) {
			vrocManager.destroyComponent(this.activeComponent.id);
			this.activeComponent = null;
			console.log('[Transfers] Active component destroyed');
		}
	}

	/**
	 * Уничтожает все компоненты
	 */
	destroyAllComponents() {
		vrocManager.destroyComponentsByName('transfers');
		this.activeComponent = null;
		console.log('[Transfers] All components destroyed');
	}

	/**
	 * Получает активный компонент
	 */
	getActiveComponent() {
		return this.activeComponent;
	}
}

// Глобальная регистрация для внешнего доступа
if (typeof window !== 'undefined') {
	window.transfers = new Transfers();
}
